import equal from 'fast-deep-equal';

import type { AIImageGenerationConfig } from '@/lib/image-generation-helpers';
import { getFreeformSectionAiConfig } from '@/lib/image-generation-helpers';
import { normalizePage } from '@/lib/page-utils';
import { isPendingAIImage, normalizeImageValue } from '@/types/image';
import type { Page } from '@/types/page';
import type { PageSection } from '@/types/page-section';

export function getNestedValue<T = unknown>(obj: unknown, path: string): T | undefined {
  if (!path.includes('.')) {
    if (obj && typeof obj === 'object') {
      return (obj as Record<string, T>)[path];
    }
    return undefined;
  }

  const parts = path.split('.');
  let value: unknown = obj;
  for (const part of parts) {
    if (value == null) return undefined;
    if (typeof value !== 'object') return undefined;
    value = (value as Record<string, unknown>)[part];
  }
  return value as T | undefined;
}

function setNestedValue(
  source: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  if (!path.includes('.')) {
    return { ...source, [path]: value };
  }

  const parts = path.split('.');
  const updated = { ...source };
  let current: Record<string, unknown> = updated;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const existing = current[key];
    const next =
      typeof existing === 'object' && existing !== null && !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>) }
        : {};
    current[key] = next;
    current = next;
  }

  current[parts[parts.length - 1]] = value;
  return updated;
}

function buildCompletedImageValue(
  currentValue: unknown,
  imageUrl: string,
): Record<string, unknown> {
  const normalized = normalizeImageValue(currentValue);

  if (normalized) {
    return {
      ...(normalized.assetId ? { assetId: normalized.assetId } : {}),
      url: imageUrl,
      ...(normalized.alt ? { alt: normalized.alt } : {}),
      ...(normalized.isInitialProp ? { isInitialProp: normalized.isInitialProp } : {}),
    };
  }

  return { url: imageUrl };
}

/**
 * Updates section props with completed image based on AI config.
 * Handles both section-level images and array item images.
 */
export function updateImageInProps(
  props: Record<string, unknown>,
  aiConfig: AIImageGenerationConfig,
  itemId: string | null,
  imageUrl: string,
): Record<string, unknown> {
  const updatedProps = { ...props };

  // Section-level image
  if (aiConfig.sectionImage && !itemId) {
    const field = aiConfig.sectionImage.field;
    const currentValue = getNestedValue(updatedProps, field);
    return setNestedValue(updatedProps, field, buildCompletedImageValue(currentValue, imageUrl));
  }

  // Array item image
  if (aiConfig.arrayItemImages && itemId) {
    const { arrayField, imageField } = aiConfig.arrayItemImages;
    const rawArray = updatedProps[arrayField];
    const array = [...(Array.isArray(rawArray) ? rawArray : [])] as Array<Record<string, unknown>>;
    const itemIndex = array.findIndex((item) => item.id === itemId);

    if (itemIndex !== -1) {
      const currentValue = getNestedValue(array[itemIndex], imageField);
      array[itemIndex] = setNestedValue(
        array[itemIndex],
        imageField,
        buildCompletedImageValue(currentValue, imageUrl),
      );
      updatedProps[arrayField] = array;
    }
  }

  return updatedProps;
}

function applyStoredUrlsToPendingItems(
  incomingItems: Array<Record<string, unknown>>,
  storedItems: Array<Record<string, unknown>>,
  imageField: string,
): { items: Array<Record<string, unknown>>; changed: boolean } {
  if (incomingItems.length === 0 || storedItems.length === 0) {
    return { items: incomingItems, changed: false };
  }

  const storedItemsById = new Map<string, Record<string, unknown>>();
  for (const storedItem of storedItems) {
    if (storedItem?.id) {
      storedItemsById.set(String(storedItem.id), storedItem);
    }
  }

  let changed = false;
  const items = incomingItems.map((item) => {
    if (!item?.id) return item;
    const storedItem = storedItemsById.get(String(item.id));
    if (!storedItem) return item;

    const incomingValue = item[imageField];
    if (!isPendingAIImage(incomingValue)) return item;

    const storedUrl = normalizeImageValue(storedItem[imageField])?.url;
    if (!storedUrl) return item;

    changed = true;
    return {
      ...item,
      [imageField]: buildCompletedImageValue(incomingValue, storedUrl),
    };
  });

  return { items, changed };
}

function applyStoredUrlsToCollectionItems(
  incomingBlocks: PageSection['blocks'],
  storedBlocks: PageSection['blocks'],
): PageSection['blocks'] | undefined {
  if (!Array.isArray(incomingBlocks) || !Array.isArray(storedBlocks)) {
    return undefined;
  }

  const storedById = new Map(storedBlocks.map((block) => [block.id, block]));
  let changed = false;

  const nextBlocks = incomingBlocks.map((block) => {
    if (block.blockType !== 'collection') return block;
    const storedBlock = storedById.get(block.id);
    if (!storedBlock) return block;

    const incomingItems = Array.isArray(block.props?.items)
      ? (block.props.items as Array<Record<string, unknown>>)
      : [];
    const storedItems = Array.isArray(storedBlock.props?.items)
      ? (storedBlock.props.items as Array<Record<string, unknown>>)
      : [];

    const merged = applyStoredUrlsToPendingItems(incomingItems, storedItems, 'image');
    if (!merged.changed) return block;

    changed = true;
    return {
      ...block,
      props: { ...block.props, items: merged.items },
    };
  });

  return changed ? nextBlocks : undefined;
}

export function mergeResolvedImagesFromStore(incomingPage: Page, storedPage: Page): Page {
  if (!incomingPage?.slots?.main?.sections || !storedPage?.slots?.main?.sections) {
    return incomingPage;
  }

  const storedSections = new Map<string, PageSection>(
    storedPage.slots.main.sections.map((section) => [section.id, section]),
  );

  let hasChanges = false;
  const mergedSections = incomingPage.slots.main.sections.map((section) => {
    const storedSection = storedSections.get(section.id);
    if (!storedSection) {
      return section;
    }

    let nextSection = section;
    let sectionChanged = false;

    const aiConfig = getFreeformSectionAiConfig(section);
    if (aiConfig && section.props && storedSection.props) {
      let nextProps = section.props;

      if (aiConfig.sectionImage) {
        const field = aiConfig.sectionImage.field;
        const incomingValue = getNestedValue(section.props, field);
        const storedValue = getNestedValue(storedSection.props, field);

        if (isPendingAIImage(incomingValue) && storedValue) {
          const storedUrl = normalizeImageValue(storedValue)?.url;
          if (storedUrl) {
            nextProps = setNestedValue(
              nextProps,
              field,
              buildCompletedImageValue(incomingValue, storedUrl),
            );
            sectionChanged = true;
          }
        }
      }

      if (aiConfig.arrayItemImages) {
        const { arrayField, imageField } = aiConfig.arrayItemImages;
        const incomingItems = Array.isArray(section.props[arrayField])
          ? (section.props[arrayField] as Array<Record<string, unknown>>)
          : [];
        const storedItems = Array.isArray(storedSection.props[arrayField])
          ? (storedSection.props[arrayField] as Array<Record<string, unknown>>)
          : [];

        const merged = applyStoredUrlsToPendingItems(incomingItems, storedItems, imageField);
        if (merged.changed) {
          nextProps = {
            ...nextProps,
            [arrayField]: merged.items,
          };
          sectionChanged = true;
        }
      }

      if (sectionChanged) {
        nextSection = { ...nextSection, props: nextProps };
      }
    }

    const mergedBlocks = applyStoredUrlsToCollectionItems(section.blocks, storedSection.blocks);
    if (mergedBlocks) {
      nextSection = { ...nextSection, blocks: mergedBlocks };
      sectionChanged = true;
    }

    if (!sectionChanged) {
      return section;
    }

    hasChanges = true;
    return nextSection;
  });

  if (!hasChanges) {
    return incomingPage;
  }

  return {
    ...incomingPage,
    slots: {
      ...incomingPage.slots,
      main: {
        ...incomingPage.slots.main,
        sections: mergedSections,
      },
    },
  };
}

export function shouldSyncPageToStore(
  nextPage: Page,
  currentStored: Page | null | undefined,
): boolean {
  if (!currentStored) return true;
  // Skip setPage when only the object identity changed (same page content).
  return !equal(normalizePage(nextPage), normalizePage(currentStored));
}
